import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../../components/ui/Sidebar';

import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PermitTypeSelector from './components/PermitTypeSelector';
import WorkDescriptionSection from './components/WorkDescriptionSection';
import LocationAssignment from './components/LocationAssignment';
import HazardIdentification from './components/HazardIdentification';
import SafetyEquipment from './components/SafetyEquipment';
import PersonnelAssignment from './components/PersonnelAssignment';
import ApprovalHierarchy from './components/ApprovalHierarchy';
import PermitPreview from './components/PermitPreview';

const WorkPermitCreationContent = () => {
  const navigate = useNavigate();
  const { isCollapsed } = useSidebar();
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const permitTypes = [
  {
    id: 'hot-work',
    name: 'Hot Work Permit',
    icon: 'Flame',
    description: 'For welding, cutting, grinding, or any work involving open flames or heat sources'
  },
  {
    id: 'confined-space',
    name: 'Confined Space Entry',
    icon: 'Box',
    description: 'For entry into tanks, vessels, pits, or any confined spaces with limited access'
  },
  {
    id: 'electrical',
    name: 'Electrical Work',
    icon: 'Zap',
    description: 'For work on electrical systems, panels, or equipment requiring lockout/tagout'
  },
  {
    id: 'height-work',
    name: 'Work at Height',
    icon: 'ArrowUp',
    description: 'For work performed above 6 feet requiring fall protection equipment'
  },
  {
    id: 'excavation',
    name: 'Excavation Permit',
    icon: 'Construction',
    description: 'For digging, trenching, or any ground disturbance activities'
  },
  {
    id: 'general',
    name: 'General Work Permit',
    icon: 'Wrench',
    description: 'For routine maintenance and general work activities'
  }];


  const locations = [
  { value: 'plant-1', label: 'Plant 1 - Main Facility' },
  { value: 'plant-2', label: 'Plant 2 - North Wing' },
  { value: 'plant-3', label: 'Plant 3 - South Wing' },
  { value: 'warehouse-1', label: 'Warehouse 1' },
  { value: 'warehouse-2', label: 'Warehouse 2' }];


  const hazardCategories = [
  {
    id: 'physical',
    name: 'Physical Hazards',
    icon: 'Hammer',
    hazards: [
    { id: 'noise', name: 'Excessive Noise', description: 'Noise levels above 85 dB' },
    { id: 'vibration', name: 'Vibration', description: 'Equipment or tool vibration' },
    { id: 'temperature', name: 'Extreme Temperature', description: 'Hot or cold environments' },
    { id: 'pressure', name: 'Pressure Systems', description: 'High pressure equipment' }]

  },
  {
    id: 'chemical',
    name: 'Chemical Hazards',
    icon: 'Droplet',
    hazards: [
    { id: 'toxic', name: 'Toxic Substances', description: 'Exposure to harmful chemicals' },
    { id: 'flammable', name: 'Flammable Materials', description: 'Fire or explosion risk' },
    { id: 'corrosive', name: 'Corrosive Materials', description: 'Skin or material damage' },
    { id: 'dust', name: 'Dust/Fumes', description: 'Airborne particles' }]

  },
  {
    id: 'electrical',
    name: 'Electrical Hazards',
    icon: 'Zap',
    hazards: [
    { id: 'shock', name: 'Electric Shock', description: 'Contact with live circuits' },
    { id: 'arc-flash', name: 'Arc Flash', description: 'Electrical arc exposure' },
    { id: 'static', name: 'Static Electricity', description: 'Static discharge risk' }]

  },
  {
    id: 'mechanical',
    name: 'Mechanical Hazards',
    icon: 'Settings',
    hazards: [
    { id: 'moving-parts', name: 'Moving Parts', description: 'Rotating or moving machinery' },
    { id: 'pinch-points', name: 'Pinch Points', description: 'Crushing or pinching risk' },
    { id: 'falling-objects', name: 'Falling Objects', description: 'Overhead hazards' }]

  }];


  const equipmentList = [
  { id: 'hard-hat', name: 'Hard Hat', description: 'Head protection', required: true },
  { id: 'safety-glasses', name: 'Safety Glasses', description: 'Eye protection', required: true },
  { id: 'steel-toe-boots', name: 'Steel Toe Boots', description: 'Foot protection', required: true },
  { id: 'gloves', name: 'Safety Gloves', description: 'Hand protection', required: true },
  { id: 'high-vis-vest', name: 'High Visibility Vest', description: 'Visibility enhancement', required: false },
  { id: 'respirator', name: 'Respirator', description: 'Respiratory protection', required: false },
  { id: 'hearing-protection', name: 'Hearing Protection', description: 'Ear plugs or muffs', required: false },
  { id: 'fall-harness', name: 'Fall Protection Harness', description: 'Fall arrest system', required: false },
  { id: 'face-shield', name: 'Face Shield', description: 'Full face protection', required: false },
  { id: 'fire-suit', name: 'Fire Resistant Clothing', description: 'Heat protection', required: false }];


  const availablePersonnel = [
  {
    value: 'john-smith',
    label: 'John Smith',
    role: 'Electrical Supervisor',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_17fe719dd-1763292172012.png",
    avatarAlt: 'Professional headshot of Caucasian male supervisor with short brown hair wearing blue work uniform and safety glasses'
  },
  {
    value: 'maria-garcia',
    label: 'Maria Garcia',
    role: 'Safety Officer',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_129aa8ebc-1763299616245.png",
    avatarAlt: 'Professional headshot of Hispanic female safety officer with long dark hair in high visibility vest and hard hat'
  },
  {
    value: 'david-chen',
    label: 'David Chen',
    role: 'Maintenance Technician',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_18e347146-1763300910646.png",
    avatarAlt: 'Professional headshot of Asian male technician with black hair wearing gray work shirt and safety equipment'
  },
  {
    value: 'sarah-johnson',
    label: 'Sarah Johnson',
    role: 'Mechanical Engineer',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1a664d521-1763299262093.png",
    avatarAlt: 'Professional headshot of Caucasian female engineer with blonde hair in white hard hat and blue work jacket'
  },
  {
    value: 'robert-williams',
    label: 'Robert Williams',
    role: 'Welder',
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1c7c570ac-1763296079697.png",
    avatarAlt: 'Professional headshot of African American male welder with short hair wearing protective welding gear and safety vest'
  }];


  const [selectedType, setSelectedType] = useState('');
  const [selectedHazards, setSelectedHazards] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [assignedPersonnel, setAssignedPersonnel] = useState([]);
  const [approvalLevels, setApprovalLevels] = useState([]);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    workTitle: '',
    workDescription: '',
    estimatedDuration: '',
    numberOfWorkers: '',
    startDateTime: '',
    endDateTime: '',
    primaryLocation: '',
    buildingArea: '',
    specificLocation: ''
  });

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleHazardToggle = (hazardId, checked) => {
    setSelectedHazards((prev) =>
    checked ? [...prev, hazardId] : prev?.filter((id) => id !== hazardId)
    );
    if (errors?.hazards) {
      setErrors((prev) => ({ ...prev, hazards: '' }));
    }
  };

  const handleEquipmentToggle = (equipmentId, checked) => {
    setSelectedEquipment((prev) =>
    checked ? [...prev, equipmentId] : prev?.filter((id) => id !== equipmentId)
    );
    if (errors?.equipment) {
      setErrors((prev) => ({ ...prev, equipment: '' }));
    }
  };

  const handleAddPersonnel = (person) => {
    setAssignedPersonnel((prev) => [...prev, person]);
    if (errors?.personnel) {
      setErrors((prev) => ({ ...prev, personnel: '' }));
    }
  };

  const handleRemovePersonnel = (personId) => {
    setAssignedPersonnel((prev) => prev?.filter((p) => p?.id !== personId));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!selectedType) {
      newErrors.permitType = 'Please select a permit type';
    }

    if (!formData?.workTitle?.trim()) {
      newErrors.workTitle = 'Work title is required';
    }

    if (!formData?.workDescription?.trim()) {
      newErrors.workDescription = 'Work description is required';
    } else if (formData?.workDescription?.trim()?.length < 50) {
      newErrors.workDescription = 'Description must be at least 50 characters';
    }

    if (!formData?.estimatedDuration?.trim()) {
      newErrors.estimatedDuration = 'Estimated duration is required';
    }

    if (!formData?.numberOfWorkers) {
      newErrors.numberOfWorkers = 'Number of workers is required';
    }

    if (!formData?.startDateTime) {
      newErrors.startDateTime = 'Start date and time is required';
    }

    if (!formData?.endDateTime) {
      newErrors.endDateTime = 'End date and time is required';
    }

    if (formData?.startDateTime && formData?.endDateTime) {
      if (new Date(formData.endDateTime) <= new Date(formData.startDateTime)) {
        newErrors.endDateTime = 'End time must be after start time';
      }
    }

    if (!formData?.primaryLocation) {
      newErrors.primaryLocation = 'Primary location is required';
    }

    if (!formData?.buildingArea?.trim()) {
      newErrors.buildingArea = 'Building/Area is required';
    }

    if (selectedHazards?.length === 0) {
      newErrors.hazards = 'Please identify at least one hazard';
    }

    const requiredEquipmentIds = equipmentList?.filter((eq) => eq?.required)?.map((eq) => eq?.id);
    const missingRequired = requiredEquipmentIds?.filter((id) => !selectedEquipment?.includes(id));
    if (missingRequired?.length > 0) {
      newErrors.equipment = 'All mandatory safety equipment must be selected';
    }

    if (assignedPersonnel?.length === 0) {
      newErrors.personnel = 'Please assign at least one person to this permit';
    }

    // Validate approval hierarchy
    if (approvalLevels?.length > 0) {
      approvalLevels?.forEach((level) => {
        if (!level?.approver) {
          newErrors[`approver_level_${level?.level}`] = `Approver for Level ${level?.level} is required`;
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSaveDraft = async () => {
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      alert('Draft saved successfully!');
    }, 1500);
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      alert('Work permit submitted successfully for approval!');
      navigate('/work-permit-tracking');
    }, 2000);
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All unsaved changes will be lost.')) {
      navigate('/module-selection-dashboard');
    }
  };

  return (
    <div className={`bg-background min-h-screen`}>
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
                aria-label="Go back"
              >
                <Icon name="ArrowLeft" size={20} className="text-foreground" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Create Work Permit</h1>
                <p className="text-muted-foreground mt-1">
                  Complete all required information to create a new work permit
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              iconName="Home"
              onClick={() => navigate('/')}
            >
              Return to Dashboard
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {errors?.permitType && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Icon name="AlertCircle" size={18} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-error">{errors?.permitType}</p>
                </div>
              </div>
            )}

            <PermitTypeSelector
              selectedType={selectedType}
              onTypeSelect={setSelectedType}
              permitTypes={permitTypes}
            />

            <WorkDescriptionSection
              formData={formData}
              onFormChange={handleFormChange}
              errors={errors}
            />

            <LocationAssignment
              formData={formData}
              onFormChange={handleFormChange}
              errors={errors}
              locations={locations}
            />

            <HazardIdentification
              selectedHazards={selectedHazards}
              onHazardToggle={handleHazardToggle}
              hazardCategories={hazardCategories}
              errors={errors}
            />

            <SafetyEquipment
              selectedEquipment={selectedEquipment}
              onEquipmentToggle={handleEquipmentToggle}
              equipmentList={equipmentList}
              errors={errors}
            />

            <PersonnelAssignment
              assignedPersonnel={assignedPersonnel}
              onAddPersonnel={handleAddPersonnel}
              onRemovePersonnel={handleRemovePersonnel}
              availablePersonnel={availablePersonnel}
              errors={errors}
            />

            <ApprovalHierarchy
              selectedType={selectedType}
              selectedHazards={selectedHazards}
              approvalLevels={approvalLevels}
              onApprovalLevelsChange={setApprovalLevels}
              errors={errors}
            />
          </div>

          <div className="lg:col-span-1">
            <PermitPreview
              formData={formData}
              selectedType={permitTypes?.find((t) => t?.id === selectedType)?.name}
              assignedPersonnel={assignedPersonnel}
              selectedHazards={selectedHazards}
              selectedEquipment={selectedEquipment}
              approvalLevels={approvalLevels}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex items-center justify-end gap-4 pb-8">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isSaving || isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            iconName="Save"
            onClick={handleSaveDraft}
            disabled={isSaving || isSubmitting}
          >
            {isSaving ? 'Saving...' : 'Save Draft'}
          </Button>
          <Button
            iconName="Send"
            onClick={handleSubmit}
            disabled={isSaving || isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit for Approval'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WorkPermitCreationContent;